import { createElement, type ReactNode } from "react";
import { ThemeProvider as MaterialUIThemeProvider } from "@material-ui/styles";
import type { DefaultTheme } from "@material-ui/styles/defaultTheme";

interface MuiThemeProviderProps<Theme = DefaultTheme> {
  theme: Partial<Theme> | ((outerTheme: Theme) => Theme);
  children: ReactNode;
}

// Wraps `@material-ui/styles` `ThemeProvider`, which validates props with
// `exactProp` and warns about anything other than `theme` and `children`. The
// `@freshpaint/babel-plugin-annotate-react` plugin (vite.config.mts) injects
// `data-element` and `data-source-file` attributes on every JSX element whose
// tag is not in its lowercase HTML allowlist, which trips that warning. By
// returning a `createElement` call (not JSX) we keep the babel plugin from
// annotating the inner provider, and destructuring `theme`/`children` here
// drops any `data-*` props the plugin injected at the call site.
export function MuiThemeProvider<Theme = DefaultTheme>({
  theme,
  children,
}: MuiThemeProviderProps<Theme>) {
  // MUI's ThemeProvider types declare `children` as required, so passing it as
  // createElement's third argument fails the type overload check. Putting it
  // in the props object is functionally equivalent and satisfies TypeScript.
  // eslint-disable-next-line react/no-children-prop
  return createElement(MaterialUIThemeProvider, { theme, children });
}
